import { FitnessReport } from "@/components/models/FitnessReport";
import { scopeSvgIds } from "@/components/models/svg-utils";
import { useProbe } from "@/components/models/use-probe";
import { ENDPOINT_LOGOS, endpointKind, type EndpointKind } from "@/components/models/vendor-logos";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api, type ModelRole, type OrgEndpointView, type PurposeDef, type VerdictStatus } from "@/lib/api";
import { keptProbeId } from "@/lib/endpoint-check";
import { ADDRESS_NOT_ALLOWED, describeModelError } from "@/lib/model-errors";
import { BUILT_IN_ONLY_NOTE } from "@/lib/probe-copy";
import { jobLabel, joinNames, ROLE_LABELS, sourceLabel, withJobNames } from "@/lib/models";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, PlugZap, X } from "lucide-react";
import { useId, useRef, useState } from "react";
import { toast } from "sonner";

// Add or edit one of the organization's own model services: anything that
// accepts OpenAI-style requests. The address comes first; the logos only
// fill it in. The ID used in settings follows the address until someone
// edits it under Advanced. Keys are stored for the organization and never
// shown again. A check runs on one of the org's running Boos; saving without
// one is fine.

// Quick fill, from the logos at the top. A hosted service fills its address;
// Azure and Bedrock fill a template with the part to change selected; servers
// people run themselves leave the address empty, since Boo can't reach their
// localhost. The name, ID and model example follow the choice.
type Preset = {
  name: EndpointKind;
  display: string;
  // Empty for servers people run themselves.
  baseUrl: string;
  // Selected after filling, for typing over.
  slot?: string;
  // The slot must be replaced before saving.
  blocking?: boolean;
  hint?: string;
  example?: string;
  model?: string;
  // Shown, not offered: no address works with a pasted key.
  displayOnly?: boolean;
};
const PRESETS: Preset[] = [
  {
    name: "azure",
    display: "Azure",
    baseUrl: "https://YOUR-RESOURCE.openai.azure.com/openai/v1",
    slot: "YOUR-RESOURCE",
    blocking: true,
    hint: "Replace YOUR-RESOURCE with your Azure resource name. Model names are your deployment names.",
    model: "gpt-4.1",
  },
  {
    name: "bedrock",
    display: "AWS Bedrock",
    baseUrl: "https://bedrock-runtime.us-east-1.amazonaws.com/openai/v1",
    slot: "us-east-1",
    hint: "Change us-east-1 if your models run in another region. Use a Bedrock API key.",
    model: "openai.gpt-oss-120b-1:0",
  },
  { name: "vertex", display: "Google Vertex", baseUrl: "", displayOnly: true },
  { name: "openai", display: "OpenAI", baseUrl: "https://api.openai.com/v1", model: "gpt-4.1" },
  { name: "mistral", display: "Mistral", baseUrl: "https://api.mistral.ai/v1", model: "mistral-large-latest" },
  {
    name: "openrouter",
    display: "OpenRouter",
    baseUrl: "https://openrouter.ai/api/v1",
    model: "meta-llama/llama-3.3-70b-instruct",
  },
  {
    name: "together",
    display: "Together",
    baseUrl: "https://api.together.xyz/v1",
    model: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
  },
  { name: "groq", display: "Groq", baseUrl: "https://api.groq.com/openai/v1", model: "llama-3.3-70b-versatile" },
  {
    name: "fireworks",
    display: "Fireworks",
    baseUrl: "https://api.fireworks.ai/inference/v1",
    model: "accounts/fireworks/models/llama-v3p3-70b-instruct",
  },
  {
    name: "huggingface",
    display: "Hugging Face",
    baseUrl: "https://router.huggingface.co/v1",
    model: "meta-llama/Llama-3.3-70B-Instruct",
  },
  {
    name: "nvidia",
    display: "NVIDIA",
    baseUrl: "https://integrate.api.nvidia.com/v1",
    model: "meta/llama-3.3-70b-instruct",
  },
  { name: "ollama", display: "Ollama", baseUrl: "", example: "https://ollama.your-company.com/v1", model: "llama3.2" },
  {
    name: "vllm",
    display: "vLLM",
    baseUrl: "",
    example: "https://vllm.your-company.com/v1",
    model: "meta-llama/Llama-3.1-8B-Instruct",
  },
  {
    name: "lmstudio",
    display: "LM Studio",
    baseUrl: "",
    example: "https://lmstudio.your-company.com/v1",
    model: "qwen2.5-7b-instruct",
  },
];

// The preset an address is exactly, else the template or self-hosted one
// chosen (a fixed address only counts while unchanged).
function presetFor(baseUrl: string, chosen: Preset | null): Preset | null {
  return (
    PRESETS.find((p) => p.baseUrl && !p.slot && p.baseUrl === baseUrl) ??
    (chosen && (!chosen.baseUrl || chosen.slot) ? chosen : null)
  );
}

const NAME_RE = /^[a-z][a-z0-9-]{1,30}$/;
const MODEL_RE = /^[A-Za-z0-9][A-Za-z0-9._:/@+-]{0,127}$/;
const ROLES: ModelRole[] = ["chat", "transcription", "embeddings", "decision"];

// Loopback, private, link-local and cloud metadata addresses: the backend
// refuses them (address_not_allowed), so the form does too.
function isLocalAddress(raw: string): boolean {
  try {
    const host = new URL(raw).hostname.toLowerCase();
    return (
      host === "localhost" ||
      host.endsWith(".localhost") ||
      host === "0.0.0.0" ||
      host === "[::1]" ||
      host === "metadata.google.internal" ||
      /^127\./.test(host) ||
      /^10\./.test(host) ||
      /^192\.168\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
      /^169\.254\./.test(host) ||
      /^\[f[cd][0-9a-f]{2}:/.test(host) ||
      /^\[fe80:/.test(host)
    );
  } catch {
    return false;
  }
}

// "https:///v1" and the like: the shape of an address, waiting for its host.
function missingHost(raw: string): boolean {
  return /^https?:\/\/\//.test(raw);
}

// "ollama.acme.com/v1" means https://; leaving the field writes it in.
function hasScheme(raw: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(raw);
}

function originOf(raw: string): string | null {
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

// An ID from the address: a Quick fill choice keeps its preset's ID;
// "https://llm.acme.example/v1" → "acme", "https://my-res.openai.azure.com"
// → "azure", "http://localhost:8000" → "local".
function suggestName(baseUrl: string, taken: string[], chosen: Preset | null): string {
  const preset = presetFor(baseUrl, chosen);
  let base = preset?.name ?? "custom";
  if (!preset) {
    try {
      const host = new URL(baseUrl).hostname.replace(/^\[|\]$/g, "");
      if (host === "localhost" || /^[\d.:]+$/.test(host)) base = "local";
      else {
        const labels = host.split(".").filter((l) => !["www", "api", "llm", "models"].includes(l));
        base = labels.length >= 2 ? labels[labels.length - 2] : (labels[0] ?? "custom");
      }
    } catch {
      /* not a URL yet */
    }
  }
  base = base.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^[^a-z]+/, "").slice(0, 28) || "custom";
  if (base.length < 2) base = `${base}-models`;
  let name = base;
  for (let i = 2; taken.includes(name); i++) name = `${base}-${i}`;
  return name;
}

// The display name the address suggests: "Acme models", "OpenRouter".
function suggestLabel(baseUrl: string, id: string, chosen: Preset | null): string {
  const preset = presetFor(baseUrl, chosen);
  if (preset) return preset.display;
  if (!id || id === "custom") return "";
  return `${sourceLabel({ name: id.replace(/-\d+$/, "") })} models`;
}

// The services this works with, as logos that fill in their address: anything
// that accepts OpenAI-style requests, which "your own servers" stands for.
function WorksWith({ picked, onPick }: { picked: Preset | null; onPick: (p: Preset) => void }) {
  const instance = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  return (
    <div className="-mx-1 flex flex-wrap items-center gap-y-1 pt-2">
      {PRESETS.map((p) => {
        const logo = (
          <span
            className="inline-flex text-[18px] leading-none text-foreground"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: scopeSvgIds(ENDPOINT_LOGOS[p.name], instance) }}
          />
        );
        return (
          <Tooltip key={p.name}>
            <TooltipTrigger asChild>
              {p.displayOnly ? (
                <span role="img" aria-label={p.display} className="inline-flex p-1">
                  {logo}
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Use ${p.display}`}
                  aria-pressed={picked === p}
                  onClick={() => onPick(p)}
                  className="inline-flex rounded-md p-1 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none aria-pressed:bg-muted aria-pressed:ring-1 aria-pressed:ring-border"
                >
                  {logo}
                </button>
              )}
            </TooltipTrigger>
            <TooltipContent>{p.displayOnly ? p.display : `Use ${p.display}`}</TooltipContent>
          </Tooltip>
        );
      })}
      <span className="ml-1.5 text-xs whitespace-nowrap text-muted-foreground">+ your own servers</span>
    </div>
  );
}

type ModelEntry = { id: string; role: ModelRole };

function ModelsEditor({
  values,
  onChange,
  draft,
  onDraft,
  defaultRole,
  showRoles,
  example = "llama-3.3-70b",
}: {
  values: ModelEntry[];
  onChange: (v: ModelEntry[]) => void;
  // What's typed but not yet added; it counts as added when saving.
  draft: string;
  onDraft: (v: string) => void;
  defaultRole: ModelRole;
  // "Used for" per model, under Advanced.
  showRoles: boolean;
  example?: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const add = () => {
    const id = draft.trim();
    if (!id) return;
    if (!MODEL_RE.test(id)) {
      setError("Model names use letters, digits and . _ : / @ + - only.");
      return;
    }
    if (!values.some((v) => v.id === id)) onChange([...values, { id, role: defaultRole }]);
    onDraft("");
    setError(null);
  };
  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <ul className="divide-y rounded-md border">
          {values.map((m) => (
            <li key={m.id} className="flex items-center gap-2 py-1 pr-1 pl-3">
              <span className="min-w-0 flex-1 truncate text-sm">{m.id}</span>
              {showRoles && (
                <Select
                  value={m.role}
                  onValueChange={(role) => onChange(values.map((v) => (v.id === m.id ? { ...v, role: role as ModelRole } : v)))}
                >
                  <SelectTrigger size="sm" className="h-7 w-60 text-xs" aria-label={`What ${m.id} is used for`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="text-xs">
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label={`Remove ${m.id}`}
                onClick={() => onChange(values.filter((v) => v.id !== m.id))}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Input
        id="endpoint-models"
        value={draft}
        placeholder={values.length ? "Another model name" : `For example ${example}`}
        onChange={(e) => {
          onDraft(e.target.value);
          setError(null);
        }}
        onBlur={add}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export type EndpointPrefill = { name: string; baseUrl: string; models: string[]; reason?: string };

export function EndpointDialog({
  open,
  onOpenChange,
  existing,
  prefill,
  initialRole = "chat",
  takenNames,
  reservedNames,
  purposeDefs,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: OrgEndpointView | null;
  // Fields to start from when adding (moving a Boo's own service over).
  prefill?: EndpointPrefill | null;
  // The role of models added here, when opened from a job's dropdown.
  initialRole?: ModelRole;
  takenNames: string[];
  reservedNames: string[];
  purposeDefs?: PurposeDef[];
  onSaved: (endpoint: OrgEndpointView) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        // The address, not the first logo above it.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          document.getElementById("endpoint-url")?.focus();
        }}
      >
        {open && (
          <EndpointForm
            existing={existing ?? null}
            prefill={prefill ?? null}
            initialRole={initialRole}
            takenNames={takenNames}
            reservedNames={reservedNames}
            purposeDefs={purposeDefs}
            onCancel={() => onOpenChange(false)}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EndpointForm({
  existing,
  prefill,
  initialRole,
  takenNames,
  reservedNames,
  purposeDefs,
  onCancel,
  onSaved,
}: {
  existing: OrgEndpointView | null;
  prefill: EndpointPrefill | null;
  initialRole: ModelRole;
  takenNames: string[];
  reservedNames: string[];
  purposeDefs?: PurposeDef[];
  onCancel: () => void;
  onSaved: (endpoint: OrgEndpointView) => void;
}) {
  const [baseUrl, setBaseUrl] = useState(existing?.baseUrl ?? prefill?.baseUrl ?? "");
  const [name, setName] = useState(existing?.name ?? prefill?.name ?? "");
  // Until someone edits the ID, it follows the address.
  const [nameTouched, setNameTouched] = useState(!!existing || !!prefill);
  const [label, setLabel] = useState(existing ? sourceLabel(existing) : prefill ? sourceLabel({ name: prefill.name }) : "");
  const [labelTouched, setLabelTouched] = useState(!!existing || !!prefill);
  const [apiKey, setApiKey] = useState("");
  const [removeKey, setRemoveKey] = useState(false);
  const [models, setModels] = useState<ModelEntry[]>(
    existing?.models ?? prefill?.models.map((id) => ({ id, role: "chat" as const })) ?? [],
  );
  const [modelDraft, setModelDraft] = useState("");
  const [testModel, setTestModel] = useState<string>(existing?.models[0]?.id ?? "");
  const [advanced, setAdvanced] = useState(false);
  // The logo picked at the top, kept while the address is typed.
  const [chosen, setChosen] = useState<Preset | null>(null);
  const urlInput = useRef<HTMLInputElement>(null);
  const probe = useProbe();

  const taken = takenNames.filter((n) => n !== existing?.name);
  // Nothing to suggest from until the address has a host.
  // What is checked and saved: the address as typed, https:// if it has none.
  const address = !baseUrl || hasScheme(baseUrl) ? baseUrl : `https://${baseUrl}`;
  const hasHost = !!address && !missingHost(address);
  const preset = presetFor(address, chosen);
  const suggestedName = hasHost || preset ? suggestName(address, [...taken, ...reservedNames], chosen) : "";
  const suggestedLabel = suggestedName ? suggestLabel(address, suggestedName, chosen) : "";
  const effectiveName = nameTouched ? name : suggestedName;
  const effectiveLabel = labelTouched ? label : suggestedLabel;
  // A model name typed but not added yet counts.
  const typed = modelDraft.trim();
  const allModels =
    typed && MODEL_RE.test(typed) && !models.some((m) => m.id === typed) ? [...models, { id: typed, role: initialRole }] : models;

  const nameError = existing
    ? null
    : !effectiveName
      ? null
      : !NAME_RE.test(effectiveName)
        ? "The ID under Advanced needs 2-31 lowercase letters, digits or dashes, starting with a letter."
        : reservedNames.includes(effectiveName)
          ? `The ID "${effectiveName}" belongs to a built-in provider. Choose another under Advanced.`
          : taken.includes(effectiveName)
            ? `The ID "${effectiveName}" is already used. Choose another under Advanced.`
            : null;
  const waitingForHost = missingHost(address);
  const urlError = (() => {
    if (!address || waitingForHost) return null;
    if (isLocalAddress(address)) return ADDRESS_NOT_ALLOWED;
    try {
      const u = new URL(address);
      return u.protocol === "http:" || u.protocol === "https:" ? null : "The address must start with http:// or https://.";
    } catch {
      return "Enter a full address, for example https://models.your-company.com/v1.";
    }
  })();
  const effectiveTestModel = allModels.some((m) => m.id === testModel) ? testModel : (allModels[0]?.id ?? "");
  // A saved key only goes to the address it was entered for (the backend
  // enforces the same rule).
  const addressChanged = !!existing && !!address && originOf(address) !== originOf(existing.baseUrl);
  const keyRequired = (!!existing?.hasKey || !!existing?.keyNeeded) && !removeKey && !apiKey && addressChanged;
  // Azure's YOUR-RESOURCE still in the address.
  const slotPending = !!preset?.blocking && !!preset.slot && address.includes(preset.slot);
  const ready = !!effectiveName && !nameError && hasHost && !urlError && !slotPending;
  const canTest = ready && !!effectiveTestModel && !keyRequired && !removeKey;
  const canSave = ready && allModels.length > 0 && !keyRequired;
  const displayName = effectiveLabel.trim() || (effectiveName ? sourceLabel({ name: effectiveName }) : "");

  // Sent with the save so a matching check's result is kept.
  const keptCheck = keptProbeId({
    checked: probe.checked,
    completed: probe.report?.status === "completed",
    removeKey,
    existing,
    name: effectiveName,
    baseUrl: address,
    apiKey,
    modelIds: allModels.map((m) => m.id),
  });

  const save = useMutation({
    mutationFn: () =>
      api.models.saveEndpoint(effectiveName, {
        // An untouched name on an existing service is kept as it is.
        ...(labelTouched && existing ? { label: effectiveLabel.trim() || null } : !existing && effectiveLabel.trim() ? { label: effectiveLabel.trim() } : {}),
        baseUrl: address,
        models: allModels,
        ...(apiKey ? { apiKey } : removeKey ? { removeKey: true } : {}),
        ...(keptCheck ? { probeId: keptCheck } : {}),
      }),
    onSuccess: (res) => {
      toast.success(existing ? "Saved." : "Added. Every Boo in your organization can use it now.");
      onSaved(res.endpoint);
    },
  });

  const runTest = () => {
    // The saved service as is: the check's result is kept on it.
    const savedAsIs = !!existing && !apiKey && address === existing.baseUrl;
    void probe.start({
      endpoint: savedAsIs ? existing.name : { name: effectiveName, baseUrl: address, ...(apiKey ? { apiKey } : {}) },
      model: effectiveTestModel,
    });
  };

  const pick = (p: Preset) => {
    setChosen(p);
    // A self-hosted choice clears an address a hosted logo filled in and
    // keeps one the person typed.
    const filledKind = endpointKind(address);
    if (p.baseUrl) setBaseUrl(p.baseUrl);
    else if (filledKind && PRESETS.some((q) => q.name === filledKind && q.baseUrl)) setBaseUrl("");
    requestAnimationFrame(() => {
      const input = p.baseUrl && !p.slot ? document.getElementById("endpoint-models") : urlInput.current;
      input?.focus();
      if (p.slot && input instanceof HTMLInputElement) {
        const at = p.baseUrl.indexOf(p.slot);
        input.setSelectionRange(at, at + p.slot.length);
      }
    });
  };

  const verdicts = probe.report?.status === "completed" ? (probe.report.verdicts ?? []) : [];

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) save.mutate();
      }}
    >
      <DialogHeader className="shrink-0 border-b px-6 pt-6 pb-4">
        <DialogTitle className="text-base">{existing ? `Edit ${sourceLabel(existing)}` : "Add your own model"}</DialogTitle>
        <DialogDescription>
          {existing
            ? "Changes apply to every Boo that uses these models."
            : "Connect a model your company runs or pays for, and every Boo in your organization can use it."}
        </DialogDescription>
        {!existing && <WorksWith picked={preset} onPick={pick} />}
      </DialogHeader>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
        {prefill?.reason && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200 ring-inset dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30">
            {prefill.reason}
          </p>
        )}

        <div className="grid gap-2">
          <Label htmlFor="endpoint-url" className="text-sm">
            Web address (URL)
          </Label>
          <Input
            ref={urlInput}
            id="endpoint-url"
            value={baseUrl}
            autoFocus={!existing}
            placeholder={preset?.example ?? "https://models.your-company.com/v1"}
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => setBaseUrl(e.target.value.trim())}
            onBlur={() => {
              if (baseUrl && !hasScheme(baseUrl)) setBaseUrl(`https://${baseUrl}`);
            }}
            className="h-12 text-[15px] md:text-[15px]"
          />
          {urlError ? (
            <p className="text-xs text-destructive">{urlError}</p>
          ) : waitingForHost ? (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Needs an address reachable from the internet, for example through a tunnel.
            </p>
          ) : preset?.hint ? (
            <p className={slotPending ? "text-xs text-amber-700 dark:text-amber-400" : "text-xs text-muted-foreground"}>
              {preset.hint}
            </p>
          ) : preset?.baseUrl ? (
            <p className="text-xs text-muted-foreground">
              Filled in for {preset.display}. Add your {preset.display} access key below.
            </p>
          ) : preset && !preset.baseUrl ? (
            <p className="text-xs text-muted-foreground">
              Boo needs an address for your {preset.display} server that it can reach from the internet, for example
              through a tunnel. It ends in /v1.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Ask your provider or IT team. It usually ends in /v1.</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="endpoint-label">Name</Label>
          <Input
            id="endpoint-label"
            value={effectiveLabel}
            placeholder={suggestedLabel || "Acme models"}
            maxLength={60}
            onChange={(e) => {
              setLabelTouched(true);
              setLabel(e.target.value);
            }}
          />
          <p className="text-xs text-muted-foreground">How these models are listed for everyone in your organization.</p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="endpoint-models">Model names</Label>
          <ModelsEditor
            values={models}
            onChange={setModels}
            draft={modelDraft}
            onDraft={setModelDraft}
            defaultRole={initialRole}
            showRoles={advanced}
            example={preset?.model}
          />
          <p className="text-xs text-muted-foreground">Exactly as your provider writes them. Press Enter to add another.</p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="endpoint-key">Access key (API key)</Label>
          <Input
            id="endpoint-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            disabled={removeKey}
            placeholder={
              keyRequired || existing?.keyNeeded
                ? "Enter the key again"
                : existing?.hasKey
                  ? "Saved. Leave blank to keep it"
                  : "Only if your provider gave you one"
            }
            onChange={(e) => setApiKey(e.target.value)}
          />
          {keyRequired ? (
            <p className="text-xs text-destructive">The address changed, so enter the key again.</p>
          ) : existing?.keyNeeded ? (
            <p className="text-xs text-destructive">The saved key can&apos;t be read any more. Enter it again.</p>
          ) : (
            <p className="text-xs text-muted-foreground">Stored encrypted. It&apos;s never shown again.</p>
          )}
          {existing?.hasKey && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="size-3.5"
                checked={removeKey}
                onChange={(e) => {
                  setRemoveKey(e.target.checked);
                  if (e.target.checked) setApiKey("");
                }}
              />
              Remove the saved key
            </label>
          )}
        </div>

        <Collapsible open={advanced} onOpenChange={setAdvanced}>
          <CollapsibleTrigger className="group flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ChevronDown className="size-4 transition-transform group-data-[state=closed]:-rotate-90" />
            Advanced
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-3">
            <Label htmlFor="endpoint-name">ID used in settings</Label>
            <Input
              id="endpoint-name"
              value={effectiveName}
              disabled={!!existing}
              placeholder="acme"
              onChange={(e) => {
                setNameTouched(true);
                setName(e.target.value.toLowerCase());
              }}
              className="max-w-xs"
            />
            <p className="text-xs text-muted-foreground">
              {existing ? "Can't be changed after adding." : "Filled in from the address. Lowercase letters, digits and dashes."}
              {models.length > 0 && " Choose what each model is used for in the list of model names."}
            </p>
          </CollapsibleContent>
        </Collapsible>
        {nameError && <p className="-mt-3 text-xs text-destructive">{nameError}</p>}

        <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Check the connection</span>
            {allModels.length > 1 && (
              <Select value={effectiveTestModel} onValueChange={setTestModel}>
                <SelectTrigger size="sm" className="max-w-56 text-xs" aria-label="Model to check">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allModels.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">
                      {m.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto"
              disabled={!canTest}
              loading={probe.running}
              onClick={runTest}
            >
              <PlugZap className="size-3.5" />
              {probe.report || probe.error ? "Check again" : "Check connection"}
            </Button>
          </div>
          {removeKey ? (
            <p className="text-xs text-muted-foreground">Save first to check it without a key.</p>
          ) : probe.error ? (
            <p className="text-xs text-destructive">{probe.error}</p>
          ) : probe.running ? (
            <p className="text-xs text-muted-foreground">Checking… about half a minute.</p>
          ) : probe.report ? (
            <p className="text-sm">{verdictSentence(verdicts, purposeDefs)}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {allModels.length === 0
                ? "Add a model name to check it. "
                : "Sends a few test requests and tells you which jobs it suits. "}
              Optional: you can save without checking.
            </p>
          )}
          {probe.report && probe.report.status === "completed" && (
            <Collapsible>
              <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <ChevronDown className="size-3.5 transition-transform group-data-[state=closed]:-rotate-90" />
                Details
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <FitnessReport report={probe.report} purposeDefs={purposeDefs && withJobNames(purposeDefs)} />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {save.isError && (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {describeModelError(save.error, { defs: purposeDefs ?? [], source: displayName })}
          </p>
        )}
      </div>

      <DialogFooter className="mx-0 mb-0 shrink-0 rounded-b-xl border-t bg-muted/30 px-6 py-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave} loading={save.isPending}>
          {existing ? "Save changes" : allModels.length > 1 ? "Add models" : "Add model"}
        </Button>
      </DialogFooter>
    </form>
  );
}

// One plain sentence from a check, in the badges' words: which jobs it
// suits and which it doesn't.
function verdictSentence(
  verdicts: Array<{ purpose: string; status: VerdictStatus }>,
  purposeDefs: PurposeDef[] | undefined,
): string {
  if (verdicts.length === 0) return "Connected.";
  // Writing code is said once, the same way everywhere.
  const coding = verdicts.some((v) => v.purpose === "coding");
  const names = (status: VerdictStatus) =>
    verdicts
      .filter((v) => v.status === status && v.purpose !== "coding")
      .map((v) => {
        const def = purposeDefs?.find((d) => d.id === v.purpose);
        return def ? jobLabel(def).toLowerCase() : v.purpose;
      });
  const good = names("pass");
  const limited = names("warn");
  const no = names("unavailable");
  const parts = [
    good.length && `Good fit for ${joinNames(good, 3)}.`,
    limited.length && `Works, with limits, for ${joinNames(limited, 3)}.`,
    no.length && `Not available for ${joinNames(no, 3)}.`,
    coding && BUILT_IN_ONLY_NOTE,
  ].filter(Boolean);
  return `Connected. ${parts.join(" ")}`;
}
